/**
 * «Regnskapsbilag»: how VIVA's card settlement is written out — on the page, in the PDF, in the Excel file and in the
 * CSV files. The server sends every amount in øre, signed as money into the VIVA account (sales positive, refunds and
 * fees negative), and has already added them up; nothing here adds kroner as decimals.
 */
import XlsxWriter from "@/util/XlsxWriter";

const CHANNELS = { TILL: "Kasse", KIOSK: "Kiosk", WEB: "Nett", QR: "Nett (QR)", TERMINAL: "Kasse/kiosk" };
const OSLO = "Europe/Oslo";
const ATHENS = "Europe/Athens";

/** «12 345,60», whole øre, no float on the way. */
function decimal(ore, groupThousands) {
    const n = Number(ore) || 0;
    const abs = Math.abs(n);
    let whole = String(Math.floor(abs / 100));
    if (groupThousands) {
        whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }
    return whole + "," + String(abs % 100).padStart(2, "0");
}

/** A calendar day «2026-10-14» as a Date at noon UTC, so no time zone can move it to another day. */
function dayDate(day) {
    const [y, m, d] = day.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12));
}

/** The calendar day «2026-10-19» a moment falls on in a time zone. */
function dayIn(iso, timeZone) {
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
        .formatToParts(new Date(iso));
    const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
    return get("year") + "-" + get("month") + "-" + get("day");
}

/** «1 betaling», «2 refusjoner», or «3 transaksjoner» when there are both. */
function counted(group) {
    const n = group.count;
    if (group.payments) { return n === 1 ? "1 betaling" : n + " betalinger"; }
    if (group.refunds) { return n === 1 ? "1 refusjon" : n + " refusjoner"; }
    return n === 1 ? "1 transaksjon" : n + " transaksjoner";
}

const VivaSettlementFormat = {
    /** «kr 1 234,50», «−kr 36,00»: the page. */
    kr(ore) {
        if (ore === null || ore === undefined || isNaN(Number(ore))) { return "–"; }
        return (Number(ore) < 0 ? "−" : "") + "kr " + decimal(ore, true);
    },

    /** «1 234,50», «-36,00»: the PDF, in characters its font is sure to have. */
    krPdf(ore) {
        return (Number(ore) < 0 ? "-" : "") + decimal(ore, true).replace(/ /g, " ");
    },

    /** «-36,00»: a number Excel in Norwegian reads as one — ASCII minus, decimal comma, no thousands separator. */
    csvNumber(ore) {
        return (Number(ore) < 0 ? "-" : "") + decimal(ore, false);
    },

    channel(channel) {
        return CHANNELS[channel] || "–";
    },

    /** «onsdag 14. oktober 2026» */
    dayLong(day) {
        return new Intl.DateTimeFormat("nb-NO", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long", year: "numeric" })
            .format(dayDate(day));
    },

    /** «ons. 14. okt.» */
    dayShort(day) {
        return new Intl.DateTimeFormat("nb-NO", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short" })
            .format(dayDate(day));
    },

    /** «14.10.2026» */
    dayNumeric(day) {
        const [y, m, d] = day.split("-");
        return d + "." + m + "." + y;
    },

    /** «14.10.2026 06:25», Norwegian time. */
    dateTime(iso) {
        if (!iso) { return "–"; }
        const parts = new Intl.DateTimeFormat("nb-NO", {
            timeZone: OSLO, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23"
        }).formatToParts(new Date(iso));
        const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
        return get("day") + "." + get("month") + "." + get("year") + " " + get("hour") + ":" + get("minute");
    },

    /** «06:25», Norwegian time. */
    clock(iso) {
        if (!iso) { return "–"; }
        return new Intl.DateTimeFormat("nb-NO", { timeZone: OSLO, hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
            .format(new Date(iso));
    },

    /** «01.10.2026–24.10.2026», or the one day. */
    period(from, to) {
        return from === to ? this.dayNumeric(from) : this.dayNumeric(from) + "–" + this.dayNumeric(to);
    },

    /** The locations a VIVA account serves, «Mølla, Mølla Ski». */
    accountName(account) {
        return account && account.locations ? account.locations.filter(Boolean).join(", ") : "";
    },

    // ── VIVA's own list ─────────────────────────────────────────────────────────────────────────────────────

    /**
     * Where VIVA's Sales Clearance list puts a day's transactions on another day. VIVA is a Greek company and counts
     * the day in Greek time, an hour ahead of Norway, so a payment cleared after 23:00 Norwegian time is on the next
     * day in that list. The payout has it on the Norwegian day, as this page does (mollapizza, 18.10.2026; VIVA's
     * download for the day did too, in summer time). Per day: `later`, what the list puts on the next day, and
     * `earlier`, what it brings in from the day before when that day is in the report. A day the list shows as it is
     * here has no entry.
     */
    vivaListMoves(report) {
        const moves = {};
        const days = (report && report.days) || [];
        for (const d of days) {
            const moved = (d.transactions || []).filter((t) => this.listedLaterAtViva(t, d.date));
            if (!moved.length) { continue; }
            const group = {
                from: d.date,
                to: dayIn(moved[0].clearedAt, ATHENS),
                count: moved.length,
                payments: moved.every((t) => t.kind === "PAYMENT"),
                refunds: moved.every((t) => t.kind !== "PAYMENT"),
                net: moved.reduce((sum, t) => sum + (Number(t.net) || 0), 0),
                references: moved.map((t) => t.reference).filter(Boolean)
            };
            (moves[d.date] = moves[d.date] || {}).later = group;
            if (days.some((other) => other.date === group.to)) {
                (moves[group.to] = moves[group.to] || {}).earlier = group;
            }
        }
        return moves;
    },

    /** Whether VIVA's Sales Clearance list has this transaction on a later day than `day`, the Norwegian one. */
    listedLaterAtViva(transaction, day) {
        return !!(transaction && transaction.clearedAt) && dayIn(transaction.clearedAt, ATHENS) > day;
    },

    /** «1 betaling (kr 353,64) står på 19.10.2026 i VIVAs liste»: for the day's row. */
    vivaListShort(move) {
        const parts = [];
        if (move.later) {
            parts.push(counted(move.later) + " (" + this.kr(move.later.net) + ") står på " + this.dayNumeric(move.later.to)
                + " i VIVAs liste");
        }
        if (move.earlier) {
            parts.push("VIVAs liste har her også " + counted(move.earlier) + " (" + this.kr(move.earlier.net) + ") fra "
                + this.dayNumeric(move.earlier.from));
        }
        return parts.join("; ");
    },

    /** The same, as a sentence that says why: for the Excel file, the CSV and the PDF's list of days. */
    vivaListRemark(move) {
        return this.vivaListShort(move) + ". VIVA regner døgnet i gresk tid.";
    },

    /** What VIVA's list shows for the day and why, one paragraph each way: for the opened day and the PDF. */
    vivaListLong(move) {
        const paragraphs = [];
        const later = move.later;
        if (later) {
            const one = later.count === 1;
            paragraphs.push(counted(later) + " ble gjort opp etter kl. 23 norsk tid: " + this.listed(later) + ". VIVA regner"
                + " døgnet i gresk tid, én time foran, så i VIVAs Sales Clearance-liste står " + (one ? "den" : "de") + " på "
                + this.dayNumeric(later.to) + ". Der er denne dagen " + this.kr(Math.abs(later.net))
                + (later.net < 0 ? " høyere" : " lavere") + ", og " + this.dayNumeric(later.to) + " like mye "
                + (later.net < 0 ? "lavere" : "høyere") + ". Utbetalingen for dagen har " + (one ? "den" : "dem")
                + " med, som denne siden.");
        }
        const earlier = move.earlier;
        if (earlier) {
            paragraphs.push("I VIVAs Sales Clearance-liste står også " + counted(earlier) + " fra "
                + this.dayNumeric(earlier.from) + " på denne dagen: " + this.listed(earlier) + ". "
                + (earlier.count === 1 ? "Den" : "De") + " ble gjort opp etter kl. 23 norsk tid, og VIVA regner døgnet i"
                + " gresk tid. Der er denne dagen derfor " + this.kr(Math.abs(earlier.net))
                + (earlier.net < 0 ? " lavere" : " høyere") + " enn her.");
        }
        return paragraphs;
    },

    /** «P4673271634, kr 353,64», or «P1 og P2, til sammen kr 700,00». */
    listed(group) {
        const refs = group.references.length > 3
            ? group.references.slice(0, 3).join(", ") + " og " + (group.references.length - 3) + " til"
            : group.references.join(", ").replace(/, ([^,]*)$/, " og $1");
        const amount = (group.count > 1 ? "til sammen " : "") + this.kr(group.net);
        return refs ? refs + ", " + amount : amount;
    },

    /** A file name without æøå or spaces: «VIVA-oppgjor_molla_2026-10-01_2026-10-24.csv». */
    fileName(prefix, account, from, to, extension) {
        const slug = this.accountName(account).toLowerCase()
            .replace(/æ/g, "ae").replace(/ø/g, "o").replace(/å/g, "a")
            .normalize("NFD").replace(/[̀-ͯ]/g, "")
            .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
        const days = from === to ? from : from + "_" + to;
        return prefix + (slug ? "_" + slug : "") + "_" + days + "." + extension;
    },

    // ── Excel ───────────────────────────────────────────────────────────────────────────────────────────────

    /**
     * The same two tables as the CSV files, as sheets «Dager» and «Transaksjoner» of one .xlsx: kroner as numbers and
     * days as dates, so Excel sums and sorts them without being told how to read them.
     */
    workbook(report) {
        const X = XlsxWriter;
        const moves = this.vivaListMoves(report);
        const days = [["Oppgjørsdato", "Betalinger", "Refusjoner (antall)", "Salg", "Refusjoner", "Gebyr", "Netto", "Merknad"]
            .map((title) => X.header(title))];
        for (const d of report.days) {
            days.push([X.date(d.date), X.whole(d.payments), X.whole(d.refunds), X.money(d.sales), X.money(d.refunded),
                X.money(d.fees), X.money(d.net), X.text(moves[d.date] ? this.vivaListRemark(moves[d.date]) : "")]);
        }
        const t = report.totals;
        days.push([X.header("Sum"), X.whole(t.payments, true), X.whole(t.refunds, true), X.money(t.sales, true),
            X.money(t.refunded, true), X.money(t.fees, true), X.money(t.net, true)]);

        const lines = [["Oppgjørsdato", "Salgstidspunkt", "Gjort opp", "Ordre", "Hvor", "Type", "Status", "Beløp", "Gebyr",
            "Netto", "Transaksjons-ID hos VIVA"].map((title) => X.header(title))];
        for (const d of report.days) {
            for (const line of d.transactions) {
                lines.push([X.date(d.date), X.dateTime(line.time), X.dateTime(line.clearedAt), X.text(line.reference),
                    X.text(this.channel(line.channel)), X.text(line.kindLabel), X.text(line.statusLabel), X.money(line.amount),
                    X.money(line.fee), X.money(line.net), X.text(line.id)]);
            }
        }
        return X.workbook([
            { name: "Dager", widths: [14, 11, 18, 14, 14, 14, 14, 80], rows: days },
            { name: "Transaksjoner", widths: [14, 17, 17, 14, 11, 16, 12, 13, 11, 13, 38], rows: lines }
        ]);
    },

    // ── CSV, for Excel ──────────────────────────────────────────────────────────────────────────────────────

    /** One clearing day per row, the period's sum last. */
    daysCsv(report) {
        const moves = this.vivaListMoves(report);
        const rows = [["Oppgjørsdato", "Betalinger", "Refusjoner (antall)", "Salg", "Refusjoner", "Gebyr", "Netto", "Merknad"]];
        for (const d of report.days) {
            rows.push([this.dayNumeric(d.date), d.payments, d.refunds, this.csvNumber(d.sales), this.csvNumber(d.refunded),
                this.csvNumber(d.fees), this.csvNumber(d.net), moves[d.date] ? text(this.vivaListRemark(moves[d.date])) : ""]);
        }
        const t = report.totals;
        rows.push(["Sum", t.payments, t.refunds, this.csvNumber(t.sales), this.csvNumber(t.refunded),
            this.csvNumber(t.fees), this.csvNumber(t.net)]);
        return toCsv(rows);
    },

    /** Every transaction, with the day and the time it cleared, so the days can be summed again in Excel. */
    transactionsCsv(report) {
        const rows = [["Oppgjørsdato", "Salgstidspunkt", "Gjort opp", "Ordre", "Hvor", "Type", "Status", "Beløp", "Gebyr",
            "Netto", "Transaksjons-ID hos VIVA"]];
        for (const d of report.days) {
            for (const t of d.transactions) {
                rows.push([this.dayNumeric(d.date), this.dateTime(t.time), t.clearedAt ? this.dateTime(t.clearedAt) : "",
                    text(t.reference), this.channel(t.channel), t.kindLabel, t.statusLabel, this.csvNumber(t.amount),
                    this.csvNumber(t.fee), this.csvNumber(t.net), text(t.id)]);
            }
        }
        return toCsv(rows);
    },

    /** Hands the browser a file to save: text, or the bytes of a PDF or an .xlsx. The BOM makes Excel read æøå as UTF-8. */
    download(content, fileName, type) {
        const blob = new Blob([type === "text/csv" ? "﻿" + content : content],
            { type: type.startsWith("text/") ? type + ";charset=utf-8" : type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
};

/** A text field Excel must not run as a formula: one starting with = + - or @ is kept as text. */
function text(value) {
    const s = value === null || value === undefined ? "" : String(value);
    return /^[=+\-@]/.test(s) ? "'" + s : s;
}

/** Semicolon-separated, as Excel in Norwegian expects, CRLF; a field with ; " or a line break is quoted. */
function toCsv(rows) {
    return rows.map((row) => row.map((value) => {
        const text = value === null || value === undefined ? "" : String(value);
        return /[;"\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
    }).join(";")).join("\r\n") + "\r\n";
}

export default VivaSettlementFormat;
